import cn from 'classnames'
import { flyout_transitionSpeed } from 'ext-ui/style/transitionSpeeds'
import React, { ReactElement } from 'react'
import Modal, { Props as ModalProps } from 'react-modal'
import styled from 'styled-components'

import CloseButton from '../CloseButton/CloseButton'
import style from './BaseFlyout.style'

export type BaseFlyoutProps = Omit<ModalProps, 'onRequestClose'> & {
  className?: string
  /** If `true`, this Modal is open */
  isOpen: boolean
  /**
   * Use the `focus-background` variant when the content behind the flyout
   * shouldn't be fully obscured (e.g. for a filters flyout).
   *
   * Use the `default` variant in all other cases.
   */
  variant?: 'default' | 'focus-background'
  /** Function to run on close button click */
  onClose: () => void
  title?: string
  subtitle?: string
}

function BaseFlyout(props: BaseFlyoutProps): ReactElement {
  const {
    className = '',
    isOpen,
    variant = 'default',
    children,
    onClose,
    title,
    subtitle,
    // ...rest
  } = props

  return (
    <Modal
      className={cn('BaseFlyout-Content', variant)}
      overlayClassName={{
        base: 'BaseFlyout-Overlay',
        afterOpen: 'BaseFlyout-Overlay-open',
        beforeClose: 'BaseFlyout-Overlay-closed',
      }}
      portalClassName={cn('BaseFlyout', className)}
      isOpen={isOpen}
      onRequestClose={onClose}
      closeTimeoutMS={parseInt(flyout_transitionSpeed)}
      bodyOpenClassName="modal-open"
      testId="flyout"
    >
      <div className="BaseFlyout-CloseBar">
        <CloseButton
          className="BaseFlyout-CloseBar-CloseButton"
          variant="dark-on-light"
          onClick={onClose}
        />
      </div>
      {(title || subtitle) && (
        <header className="BaseFlyout-Header">
          {title && <h2>{title}</h2>}
          {subtitle && <p>{subtitle}</p>}
        </header>
      )}
      <div className="BaseFlyout-Body">{children}</div>
    </Modal>
  )
}

// We have to use `styled` instead of the `css` prop because the `css` prop
// will automatically put the styled-components mangled className in the
// `className` prop on `<Flyout>`. However, that puts the class on the Content,
// preventing us from styling the Overlay. By using `styled`, we are able to
// pass the className to `portalClassName`, which will style the parent of both
// Overlay and Content.
export default styled(BaseFlyout)`
  ${style}
`
